
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Code, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface ShortcutExample {
  code: string;
  description: string;
  example?: string;
}

const ShortcutsPage = () => {
  const navigate = useNavigate();
  
  const basicShortcuts: ShortcutExample[] = [
    { code: '5', description: 'Set next action date to 5 days from now' },
    { code: 'd:14', description: 'Set next action date to 14 days from now' },
    { code: 'p:high', description: 'Set priority to High' },
    { code: 'p:medium', description: 'Set priority to Medium' },
    { code: 'p:low', description: 'Set priority to Low' },
    { code: 's:active', description: 'Set stage to Active' },
    { code: 's:next', description: 'Set stage to Next' },
    { code: 's:waiting', description: 'Set stage to Waiting' },
    { code: 's:completed', description: 'Set stage to Completed' },
  ];

  const combinedShortcuts: ShortcutExample[] = [
    { code: '5 p:high', description: 'Set date to 5 days from now and priority to High', example: 'Quick follow-up with high priority' },
    { code: 'd:14 s:waiting', description: 'Set date to 14 days from now and mark as Waiting', example: 'Wait two weeks before follow-up' },
    { code: 'p:medium s:next', description: 'Set priority to Medium and stage to Next', example: 'Queue up as next task with medium priority' },
  ];

  return (
    <div className="container mx-auto py-6 px-4">
      <Helmet>
        <title>Shortcuts Reference | CRM</title>
      </Helmet>
      
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Command Shortcuts</h1>
          <p className="text-muted-foreground mt-2">
            Quick reference guide for task command shortcuts
          </p>
        </div>
        
        {/* Admin can access the Shortcuts Manager */}
        <Button onClick={() => navigate('/admin/shortcuts-manager')}>
          Manage Shortcuts
        </Button>
      </div>
      
      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Code className="mr-2 h-5 w-5" />
              Basic Shortcuts
            </CardTitle>
            <CardDescription>
              These basic commands can be used in the shortcut field of tasks
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[150px]">Command</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {basicShortcuts.map((shortcut) => (
                  <TableRow key={shortcut.code}>
                    <TableCell className="font-mono">
                      {shortcut.code}
                    </TableCell>
                    <TableCell>{shortcut.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Code className="mr-2 h-5 w-5" />
              Combined Commands
            </CardTitle>
            <CardDescription>
              Combine multiple shortcuts for more efficiency
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[150px]">Command</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Example Use Case</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {combinedShortcuts.map((shortcut) => (
                  <TableRow key={shortcut.code}>
                    <TableCell className="font-mono">
                      {shortcut.code}
                    </TableCell>
                    <TableCell>{shortcut.description}</TableCell>
                    <TableCell className="text-muted-foreground">{shortcut.example}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <HelpCircle className="mr-2 h-5 w-5" />
              How to Use Shortcuts
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-medium mb-2">1. In Tasks View</h3>
              <p className="text-muted-foreground">
                Use the shortcut input field in each task row to quickly update task properties.
                Just type the command and press Enter.
              </p>
            </div>
            
            <div>
              <h3 className="font-medium mb-2">2. Combining Commands</h3>
              <p className="text-muted-foreground">
                You can combine multiple commands by separating them with spaces.
                For example: <code className="bg-muted px-1.5 py-0.5 rounded">5 p:high s:active</code>
              </p>
            </div>
            
            <div className="mt-4 p-4 border rounded-md bg-muted/50">
              <p className="flex items-center text-sm">
                <HelpCircle className="h-4 w-4 mr-2 text-muted-foreground" />
                <span>
                  Administrators can create and manage custom shortcuts in the <a href="/admin/shortcuts-manager" className="text-blue-600 hover:underline">Shortcuts Manager</a>.
                </span>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ShortcutsPage;
